import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useInView } from 'react-intersection-observer';
import emailjs from '@emailjs/browser';
import { trackEvent } from './Analytics.js'; // Import the trackEvent function

function Contact() {
  // Animation trigger when section comes into view
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [validated, setValidated] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  // Initialize EmailJS
  useEffect(() => {
    // Initialize with your public key
    emailjs.init("z2u7qm-kh2QkbvQZ1");
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset status
    setSubmitStatus(null);
    
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    setValidated(true);
    setIsSubmitting(true);
    
    try {
      // Log the data we're about to send
      console.log('Sending form data:', formData);

      // Prepare template parameters for EmailJS
      const templateParams = {
        from_name: formData.name,
        reply_to: formData.email,
        phone_number: formData.phone,
        message: formData.message
      };
      
      // Send email directly using EmailJS browser SDK
      const response = await emailjs.send(
        'service_wx9s984', // Your EmailJS service ID
        'template_57na80m', // Your EmailJS template ID
        templateParams
      );
      
      console.log('EmailJS Response:', response);
      
      if (response.status === 200) {
        // Track the successful form submission
        trackEvent('contact_form_submit', {
          'form_name': 'contact_form'
        });

        // Show success message
        setSubmitStatus({
          type: 'success',
          message: 'Your message has been sent successfully. We will get back to you soon!'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
        setValidated(false);
        
        // Reset form using the ref
        if (formRef.current) {
          formRef.current.reset();
        }
      } else {
        throw new Error('Failed to send message. Please try again.');
      }
    } catch (error) {
      // Track the error
      trackEvent('contact_form_error', {
        'error_type': error.message
      });

      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'danger',
        message: 'Communication error. Please try contacting us through WhatsApp or email directly at yoga.nachiketa@gmail.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open WhatsApp chat with tracking
  const openWhatsApp = () => {
    // Track the WhatsApp button click
    trackEvent('whatsapp_contact_click');
    
    // Original WhatsApp logic
    const phone = '919173935040';
    const message = 'Hi, I\'m interested in yoga classes';
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div id="contact" className="contact" ref={ref}>
      <Container>
        <Row className="justify-content-center">
          <Col md={12}>
            <div className={`titlepage text-center ${inView ? 'animate__animated animate__fadeInDown' : ''}`}>
              <h2>Contact Us</h2>
              <span>We'd love to hear from you</span>
            </div>
          </Col>
        </Row>
        
        <Row className={`align-items-stretch gap-lg-0 gap-4 ${inView ? 'animate__animated animate__fadeIn' : ''}`}>
          {/* Contact Information */}
          <Col lg={5} md={12} className="mb-4 mb-lg-0">
            <div className="contact-info-card">
              <div className="contact-info-header">
                <h3>Get In Touch</h3>
                <p className="lead-text">Have questions about our classes or want to join? Reach out to us.</p>
              </div>
              
              <div className="contact-info-body">
                <div className="contact-item">
                  <div className="contact-icon">
                    <i className="fa fa-map-marker"></i>
                  </div>
                  <div className="contact-detail">
                    <h4>Location</h4>
                    <p>Bopal, Ahmedabad - 380058, India </p>
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">
                    <i className="fa fa-phone"></i>
                  </div>
                  <div className="contact-detail">
                    <h4>Phone</h4>
                    <p><a href="tel:+919879852728">+91 Click Me to Dial</a></p>
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">
                    <i className="fa fa-envelope"></i>
                  </div>
                  <div className="contact-detail">
                    <h4>Email</h4>
                    <p><a href="mailto:yoga.nachiketa@gmail.com">yoga.nachiketa@gmail.com</a></p>
                  </div>
                </div>
                
              </div>
              
              <div className="contact-social">
                <div className="whatsapp-button-container">
                  <Button 
                    variant="success" 
                    className="whatsapp-btn" 
                    onClick={openWhatsApp}
                  >
                    <i className="fa fa-whatsapp me-2"></i> Chat on WhatsApp
                  </Button>
                </div>
                
                <div className="social-links-container">
                  <h4>Follow Us</h4>
                  <div className="social-links">
                    <a href="https://www.instagram.com/mkailashba/" target="_blank" rel="noopener noreferrer" className="social-link">
                      <i className="fa fa-instagram"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Col>
          
          {/* Contact Form */}
          <Col lg={7} md={12}>
            <div className="contact-form-card">
              <h3><i className="fa fa-paper-plane me-2"></i>Send a Message</h3>
              
              {submitStatus && (
                <Alert variant={submitStatus.type} className="mb-4">
                  <i className={`fa ${submitStatus.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2`}></i>
                  {submitStatus.message}
                </Alert>
              )}
              
              <Form ref={formRef} noValidate validated={validated} onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3 form-group" controlId="contactName">
                      <Form.Label>Your Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter your name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="form-control-custom"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide your name.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3 form-group" controlId="contactEmail">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter your email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-control-custom"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a valid email.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-3 form-group" controlId="contactPhone">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Enter your phone number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-control-custom"
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide your phone number.
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-4 form-group" controlId="contactMessage">
                  <Form.Label>Your Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    placeholder="What would you like to know?"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="form-control-custom"
                  />
                  <Form.Control.Feedback type="invalid">
                    Please enter your message.
                  </Form.Control.Feedback>
                </Form.Group>
                
                <div className="text-center">
                  <Button 
                    type="submit"
                    className="send-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-paper-plane me-2"></i>
                        Send Message
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
        
        {/* Google Maps with Dark Theme */}
        <Row className={`mt-5 mb-3 ${inView ? 'animate__animated animate__fadeInUp animate__delay-1s' : ''}`}>
          <Col md={12}>
            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3671.8198948023883!2d72.45689091496854!3d23.03041968494776!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e9b0004401961%3A0x9e5d037ecc0e0e1c!2sNachiketa%20Yoga!5e0!3m2!1sen!2sin!4v1718496943648!5m2!1sen!2sin"
                width="100%"
                height="400"
                style={{ border: 0, borderRadius: '15px', marginTop: '10px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Nachiketa Yoga Location"
              ></iframe>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Contact;
